/**
 * @module lib/commercial/dfp-ads/gpt-ads
 * @summary Google Publisher Tag Ads
 * @description The Google Publisher Tag (GPT) is an ad tagging library for DFP
 * that can dynamically build ad requests. GPT takes key details such as ad unit name, ad
 * size, and custom targeting, builds the request, and displays the ad on web pages.
 * Refer to paired file for additional documentation.
 * @see module:lib/commercial/dfp-ads/index
 */
import isNil from "lodash/isNil";
import { isLoggedIn, getUserHashId } from "lib/identity/api";
import loadScript from "utils/load-script";
import { isFunctionThenInvoke } from "utils/utils";
import logger from "utils/logger";

const debugTag = "[DFP-ADS]";

/**
 * @method gptAttachScript
 * @description Asynchronously attaches gpt.js to the page.
 */
export function gptAttachScript() {
  logger.info(`${debugTag} attaching GPT script`);

  loadScript("https://www.googletagservices.com/tag/js/gpt.js")
    .then(() => logger.info(`${debugTag} GPT script loaded`))
    .catch(e => logger.info(`${debugTag} GPT script failed to load`, e));
}

/**
 * @method gptEnableServices
 * @description Enables collapsing of slot divs so that they don't take up any space on the page,
 * when there is no ad content to display. Enables all GPT services that have been defined for ad slots on the page.
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.enableServices
 */
export function gptEnableServices() {
  const userId = isLoggedIn() ? getUserHashId() : undefined;
  logger.info(`${debugTag} enabling GPT services - userId:`, userId);
  if (!isNil(userId)) {
    window.googletag.pubads().setPublisherProvidedId(userId);
  }
  window.googletag.enableServices();
}

/**
 * @method gptSlotCreate
 * @description Defines a googletag.slot
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.Slot
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.defineSlot
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.pubads
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService
 * @param {String} id - ID of the div that will contain this ad unit.
 * @param {String} adUnitPath - full path of the ad unit with the network code and unit code
 * @param {Array} sizeMapping - Width and height of the added slot.
 * @returns {Object} of googletag.slot
 */
export function gptSlotCreate(id, adUnitPath, sizeMapping) {
  return window.googletag.defineSlot(adUnitPath, [0, 0], id)
    .defineSizeMapping(sizeMapping)
    .setCollapseEmptyDiv(true)
    .addService(window.googletag.pubads());
}

/**
 * @method gptSetTarget
 * @description Sets custom targeting parameters for a given key that apply to all pubads service ad slots.
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_setTargeting
 * @param {String} key - Targeting parameter key.
 * @param {String|Array} value - Targeting parameter value or array of values.
 * @returns {Object} of googletag.PubAdsService - The service object on which the method was called.
 */
export function gptSetTarget(key, value) {
  logger.info(`${debugTag} target:`, key, value);
  return window.googletag.pubads().setTargeting(key, value);
}

/**
 * @method gptGetTarget
 * @description Returns a specific custom service-level targeting parameter that has been set.
 * @see https://developers.google.com/publisher-tag/reference#googletag.PubAdsService_getTargeting
 * @param {String} key - Targeting parameter key.
 * @returns {Array<String>}
 */
export function gptGetTarget(key) {
  return window.googletag.pubads().getTargeting(key);
}

/**
 * @method gptSizeMapping
 * @description Create a "size mapping" for GPT ads.
 * Builds a size map specification from the mappings added to this builder.
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.Slot_defineSizeMapping
 * @param {Array} map - of the size mappings in a GPT format, with three tiers defined.
 * @returns {Object} of googletag.SizeMappingArray - See GPT docs.
 */
export function gptSizeMapping(map) {
  const gtsm = window.googletag.sizeMapping();
  map.forEach(size => {
    gtsm.addSize([size.minWidth, 0], size.sizes);
  });
  return gtsm.build();
}

/**
 * @method gptSlotEvent
 * @description Subscribe to a specific ad slot GPT event.
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.Service_addEventListener
 * @param {String} slotId - unique ad slot identifier
 * @param {String} eventType - A string representing the type of event generated by GPT.
 * @param {Function} listener - Function that takes a single event object argument.
 */
export function gptSlotEvent(slotId, eventType, listener) {
  window.googletag.pubads().addEventListener(eventType, event => {
    if (slotId === event.slot.getSlotElementId()) {
      isFunctionThenInvoke(listener, [event, slotId]);
    }
  });
}

/**
 * @method gptGetSlots
 * @description Get the list of slots associated with this service.
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.Service_getSlots
 * @returns {Array<Object>} - of googletag.Slot
 */
export function gptGetSlots() {
  return window.googletag.pubads().getSlots();
}

/**
 * @method gptSlotRefresh
 * @description Fetches and displays new ads for specific or all slots on the page.
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_refresh
 * @param {Object} slot - googletag.slot
 * @param {Object} options
 */
export function gptSlotRefresh(slot, options = {}) {
  logger.info(`${debugTag} refreshing slot`, slot.getSlotElementId());
  window.googletag.pubads().refresh([slot], options);
}

/**
 * @method gptSlotDisplay
 * @description Instructs slot services to render the slot. Because we're
 * calling "googletag.pubads().disableInitialLoad()" immediately after the GPT
 * script loads, calling "googletag.display()" is no longer sufficient in order
 * to display an ad. We must subsequently call "googletag.pubads().refresh()".
 * @param {Object} slot - googletag.slot
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.display
 * @see https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_disableInitialLoad
 */
export function gptSlotDisplay(slot) {
  window.googletag.display(slot);
  gptSlotRefresh(slot, { changeCorrelator: false });
}

/**
 * @method gptSlotDestroy
 * @description Destroys the given slots, removing all related objects and references of those slots from GPT.
 * @param {Object} slot - googletag.slot
 * @returns {Boolean} true if slots have been destroyed, false otherwise.
 */
export function gptSlotDestroy(slot) {
  logger.info(`${debugTag} destroying slot`, slot.getSlotElementId());
  return window.googletag.destroySlots([slot]);
}
